import axios from 'axios';
import {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Loading from '../../common/Loading';
import Error from '../../common/Error';
import EditStatus from '../../common/EditStatus';
import EditRegion from '../../common/EditRegion';
import EditCountry from '../../common/EditCountry';


import { useSessionContext } from '../../../contexts/SessionContext';

export default function SuppliersEdit(props) {
    //console.debug("RTS Break SuppliersEdit");

    const theme = useTheme();
    const smallForm = useMediaQuery(theme.breakpoints.down("sm"));

    const {user, sessionCustomer, userAuthToken, apiRoot} = useSessionContext();

    const history = useNavigate();
    //const display = props.display;
    const mode = props.mode;

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorBody, setErrorBody] = useState("");
    const [errorTitle, setErrorTitle] = useState("Validation Error");

    const [idGuid, setIdGuid] = useState(null);
    const [name, setName] = useState('');
    const [shortName, setShortName] = useState('');
    const [mailAddress1, setMailAddress1] = useState('');
    const [mailAddress2, setMailAddress2] = useState('');
    const [mailCity, setMailCity] = useState('');
    const [mailRegion, setMailRegion] = useState(null);
    const [mailZip, setMailZip] = useState('');
    const [mailCountry, setMailCountry] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [status, setStatus] = useState(1);


    const fetchData = useCallback((pIdGuid) => {
        let apiURL = apiRoot + "/Suppliers/GetSupplierById/" + pIdGuid;
        axios
        .get(
            apiURL,
            {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
        )
        .then(response => {
            if (response.data) {
                setIdGuid(response.data.idGuid);
                setName(response.data.name);
                setShortName(response.data.shortName);
                setMailAddress1(response.data.mailAddress1);
                setMailAddress2(response.data.mailAddress2);
                setMailCity(response.data.mailCity);
                setMailRegion(response.data.mailRegion);
                setMailZip(response.data.mailZip);
                setMailCountry(response.data.mailCountry);
                setPhoneNumber(response.data.phoneNumber);
                setStatus(response.data.status);

                setIsLoading(false);
            }
        })
        .catch(error => {
            setHasError(true);
            console.log("RTS Data Error", error);
        });
    },[apiRoot, userAuthToken.token]); 
    
    
    useEffect(() => {
        if(mode === 1){
            setIdGuid(props.idGuid);
            fetchData(props.idGuid);
        }
        else{
            setIsLoading(false);
        }
    }, [mode, fetchData, props.idGuid]);  


    function handleSubmit(e) {
        e.preventDefault();

        let apiObj = {
            IdGuid: idGuid,
            Customer: sessionCustomer.customer,
            Name: name===''?null:name,
            ShortName: shortName===''?null:shortName,
            MailAddress1: mailAddress1===''?null:mailAddress1,
            MailAddress2: mailAddress2===''?null:mailAddress2,
            MailCity: mailCity===''?null:mailCity,
            MailRegion: mailRegion,
            MailZip: mailZip===''?null:mailZip,
            MailCountry: mailCountry,
            PhoneNumber: phoneNumber===''?null:phoneNumber,
            ModifyUser: user.userName,
            Status: status,
        }

        if(mode > 0){
            let apiURL = apiRoot + "/Suppliers/UpdateSupplier/" + idGuid;
            axios
            .put(
                apiURL,
                apiObj, 
                {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
            )
            .then(response => {
                history(-1);
            })
            .catch(error => {
                setErrorTitle("Update Error");
                setErrorBody("An error has occured.  Please review the console log for details.");
                console.log("RTS UpdateSupplier Error", error);
                setShowErrorModal(true);
            });
        }
        else{
            apiObj.createUser = user.userName;
            let apiURL = apiRoot + "/Suppliers/CreateSupplier";
            axios
            .post(
                apiURL,
                apiObj, 
                {headers:{'Authorization': 'Bearer ' + userAuthToken.token }}
            )
            .then(response => {
                history(-1);
            })
            .catch(error => {
                setErrorTitle("Insert Error");
                setErrorBody("An error has occured.  Please review the console log for details.");
                console.log("RTS CreateSupplier Error", error);
                setShowErrorModal(true);
            });
        }
    } 


    if(hasError){
        return(
            <Error />
        );
    };
    
    
    if(isLoading){
        return(
            <Loading />
        );
    };   


    return (
        <>
        <Dialog open={showErrorModal} onClose={()=>{setShowErrorModal(false);}}>
            <DialogTitle>{errorTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText>{errorBody}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={e => {setShowErrorModal(false);}}>Close</Button>
            </DialogActions>
        </Dialog>  
        <Box sx={{width:'100%'}}>
            <form id="SuppliersForm" onSubmit={handleSubmit}>
                <Box maxWidth="md" sx={{display:'flex', alignItems:'center', justifyContent:'center'}}>
                <Box id="smBox" maxWidth="sm" sx={{width:smallForm?'auto':'600px'}}>
                <TextField
                    id="tbName"
                    margin="normal"
                    fullWidth
                    label="Name"
                    size="small"
                    required
                    value={name}
                    onChange={(e) => {props.setFormDirtyFx(1); setName(e.target.value);}}
                />
                <TextField
                    id="tbShortName"
                    margin="normal"
                    fullWidth
                    label="Short Name"
                    size="small"
                    required
                    value={shortName}
                    onChange={(e) => {props.setFormDirtyFx(1); setShortName(e.target.value);}}
                />
                <Typography variant="h6" sx={{mt:2, mb:2}}>Address</Typography>
                <TextField
                    id="tbAddress1"
                    margin="none"
                    fullWidth
                    label="Street Address"
                    size="small"
                    value={mailAddress1}
                    onChange={(e) => {props.setFormDirtyFx(1); setMailAddress1(e.target.value);}}
                />
                <TextField
                    id="tbAddress2"
                    margin="dense"
                    fullWidth
                    size="small"
                    value={mailAddress2}
                    onChange={(e) => {props.setFormDirtyFx(1); setMailAddress2(e.target.value);}}
                />
                <TextField
                    id="tbCity"
                    margin="normal"
                    fullWidth
                    label="City"
                    size="small"
                    value={mailCity}
                    onChange={(e) => {props.setFormDirtyFx(1); setMailCity(e.target.value);}}
                />
                <Grid container>
                    <Grid item xs={6} sx={{pr:1}}>
                        <EditRegion region={mailRegion} setRegionFx={setMailRegion} />
                    </Grid>
                    <Grid item xs={6} sx={{pl:1}}>
                        <TextField
                            id="tbZip"
                            margin="normal"
                            fullWidth
                            label="Zip Code"
                            size="small"
                            value={mailZip}
                            onChange={(e) => {props.setFormDirtyFx(1); setMailZip(e.target.value);}}
                        />
                    </Grid>
                </Grid>
                <EditCountry country={mailCountry} setCountryFx={setMailCountry} setFormDirtyFx={props.setFormDirtyFx} />
                <TextField
                    id="tbPhoneNumber"
                    margin="normal"
                    fullWidth
                    label="Phone Number"
                    size="small"
                    value={phoneNumber}
                    onChange={(e) => {props.setFormDirtyFx(1); setPhoneNumber(e.target.value);}}
                /> 
                <EditStatus status={status} setStatusFx={setStatus} setFormDirtyFx={props.setFormDirtyFx} /> 
                </Box>
                </Box>
                <Box sx={{textAlign:'center', mt:3}}>
                    <Button variant="contained" disabled={props.formDirty===1?false:true} type="submit" sx={{mr:2}}>Save</Button>
                </Box>
            </form>
        </Box>
        </>
    );
}

